const parseTimeslotToDate = (dateTimeslot) => {
  if (!dateTimeslot) return null;
  const dateObj = new Date(dateTimeslot);
  return Number.isNaN(dateObj.getTime()) ? null : dateObj;
};

const calculateHoursBetween = (date1, date2) => {
  if (!date1 || !date2) return null;
  const diffMs = Math.abs(date1.getTime() - date2.getTime());
  return diffMs / (1000 * 60 * 60);
};

const resolveCurrentTripDateTime = (groupDetails, dateTime, trips) => {
  let dateTimeslot = null;

  if (groupDetails?.date_timeslot) {
    dateTimeslot = groupDetails.date_timeslot;
  } else if (dateTime) {
    const [date, time] = dateTime.split(' - ');
    const [day, month, year] = date.split('/');
    const [hour, minute] = time.slice(0, -2).split(':');
    const period = time.slice(-2);
    dateTimeslot = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10) % 12 + (period === 'PM' ? 12 : 0),
      parseInt(minute, 10)
    );
  } else if (trips?.[0]?.grouped_trip?.date_timeslot) {
    dateTimeslot = trips[0].grouped_trip.date_timeslot;
  }

  return parseTimeslotToDate(dateTimeslot);
};

const formatDateAndTimeslot = (dateObj) => {
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  let hour = dateObj.getHours();
  const minute = dateObj.getMinutes();
  const period = hour >= 12 ? 'PM' : 'AM';
  if (hour > 12) hour -= 12;
  if (hour === 0) hour = 12;
  const formattedTimeslot = `${hour}:${String(minute).padStart(2, '0')}${period}`;

  return { formattedDate, formattedTimeslot };
};

const isActiveTripForDriver = (groupedTrip, groupDetails) => {
  const status = groupedTrip.status;
  if (status === 'complete' || status === 4 || groupedTrip.deleted_at != null) return false;
  if (groupDetails && groupedTrip.id === groupDetails.id) return false;
  return true;
};

const isTripType = (tripType, name, code) =>
  tripType === code || tripType === name ||
  (typeof tripType === 'string' && tripType.toLowerCase() === name);

const isWithinOneDayOf = (dateA, dateB) => {
  const a = new Date(dateA);
  a.setHours(0, 0, 0, 0);
  const b = new Date(dateB);
  b.setHours(0, 0, 0, 0);
  const daysDiff = Math.abs(a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24);
  return daysDiff <= 1;
};

const buildConflictEntry = ({driver, groupId, existingTrip, existingDateTime, currentDateTime, currentCompanyAddress, existingCompanyAddress, conflictType, thresholdHours, existingTripKey}) => {
  const hoursBetween = calculateHoursBetween(currentDateTime, existingDateTime);
  if (hoursBetween === null || hoursBetween >= thresholdHours) return null;

  const existingFormatted = formatDateAndTimeslot(new Date(existingDateTime));
  const currentFormatted = formatDateAndTimeslot(new Date(currentDateTime));

  return {
    driver,
    conflictType,
    thresholdHours,
    [existingTripKey]: {
      id: existingTrip.id,
      timeslot: existingFormatted.formattedTimeslot,
      date: existingFormatted.formattedDate,
      companyAddress: existingCompanyAddress,
    },
    currentTrip: {
      id: groupId,
      timeslot: currentFormatted.formattedTimeslot,
      date: currentFormatted.formattedDate,
      companyAddress: currentCompanyAddress,
    },
    hoursBetween,
  };
};

const findInboundToOutboundConflicts = ({driver, groupId, groupDetails, dateTime, bound, trips, getDriverTrips}) => {
  if (!driver || driver.id === -1) return [];
  if (bound?.toLowerCase() !== 'outbound') return [];

  let currentCompanyAddress = groupDetails?.company_address || trips?.[0]?.location;
  if (!currentCompanyAddress) return [];

  const driverTrips = getDriverTrips(driver.user_id);
  if (!driverTrips?.length) return [];

  const currentTripDateTime = resolveCurrentTripDateTime(groupDetails, dateTime, trips);
  if (!currentTripDateTime) return [];

  const inboundTrips = driverTrips.filter((groupedTrip) => {
    if (!isActiveTripForDriver(groupedTrip, groupDetails)) return false;
    return isTripType(groupedTrip.trip_type, 'inbound', 0);
  });

  const conflicts = [];

  for (const inboundTrip of inboundTrips) {
    const inboundCompanyAddress = inboundTrip.company_address || inboundTrip.destination;
    if (!inboundCompanyAddress) continue;

    // Only check trips at different company addresses
    if (inboundCompanyAddress.toLowerCase().trim() === currentCompanyAddress.toLowerCase().trim()) continue;

    const inboundTripDateTime = parseTimeslotToDate(inboundTrip.date_timeslot);
    if (!inboundTripDateTime) continue;
    if (!isWithinOneDayOf(inboundTripDateTime, currentTripDateTime)) continue;

    // Only check if outbound trip comes AFTER inbound trip
    if (currentTripDateTime <= inboundTripDateTime) continue;

    const conflict = buildConflictEntry({
      driver, groupId, currentCompanyAddress,
      existingTrip: inboundTrip,
      existingDateTime: inboundTripDateTime,
      currentDateTime: currentTripDateTime,
      existingCompanyAddress: inboundCompanyAddress,
      conflictType: 'inbound-to-outbound',
      thresholdHours: 1,
      existingTripKey: 'inboundTrip',
    });
    if (conflict) conflicts.push(conflict);
  }

  return conflicts;
};

const findOutboundToInboundConflicts = ({driver, groupId, groupDetails, dateTime, bound, trips, getDriverTrips}) => {
  if (!driver || driver.id === -1) return [];
  if (bound?.toLowerCase() !== 'inbound') return [];

  const currentCompanyAddress = groupDetails?.company_address || trips?.[0]?.destination;
  if (!currentCompanyAddress) return [];

  const driverTrips = getDriverTrips(driver.user_id);
  if (!driverTrips?.length) return [];

  const currentTripDateTime = resolveCurrentTripDateTime(groupDetails, dateTime, trips);
  if (!currentTripDateTime) return [];

  const outboundTrips = driverTrips.filter((groupedTrip) => {
    if (!isActiveTripForDriver(groupedTrip, groupDetails)) return false;
    return isTripType(groupedTrip.trip_type, 'outbound', 1);
  });

  const conflicts = [];

  for (const outboundTrip of outboundTrips) {
    const outboundCompanyAddress = outboundTrip.company_address || outboundTrip.location;
    if (!outboundCompanyAddress) continue;

    // No address comparison — rule applies regardless of same or different address

    const outboundTripDateTime = parseTimeslotToDate(outboundTrip.date_timeslot);
    if (!outboundTripDateTime) continue;
    if (!isWithinOneDayOf(outboundTripDateTime, currentTripDateTime)) continue;

    // Only check if outbound trip comes BEFORE inbound trip
    if (outboundTripDateTime >= currentTripDateTime) continue;

    const conflict = buildConflictEntry({
      driver, groupId, currentCompanyAddress,
      existingTrip: outboundTrip,
      existingDateTime: outboundTripDateTime,
      currentDateTime: currentTripDateTime,
      existingCompanyAddress: outboundCompanyAddress,
      conflictType: 'outbound-to-inbound',
      thresholdHours: 3,
      existingTripKey: 'outboundTrip',
    });
    if (conflict) conflicts.push(conflict);
  }

  return conflicts;
};

export const checkAllTimeBasedConflicts = ({driver, groupId, groupDetails, dateTime, bound, trips, getDriverTrips}) => {
  const args = {driver, groupId, groupDetails, dateTime, bound, trips, getDriverTrips};
  const conflicts = [
    ...findInboundToOutboundConflicts(args),
    ...findOutboundToInboundConflicts(args),
  ];

  // Deduplicate conflicts based on unique combination of driver, existing trip, and current trip
  const uniqueConflicts = [];
  const seen = new Set();

  for (const conflict of conflicts) {
    // Create a unique key for each conflict — use the existing trip (inbound or outbound) details
    const existingTrip = conflict.inboundTrip || conflict.outboundTrip;
    const key = `${conflict.driver?.user_id || conflict.driver?.id}_${existingTrip.date}_${existingTrip.timeslot}_${existingTrip.companyAddress}_${conflict.currentTrip.date}_${conflict.currentTrip.timeslot}_${conflict.currentTrip.companyAddress}`;
    
    if (!seen.has(key)) {
      seen.add(key);
      uniqueConflicts.push(conflict);
    }
  }
  
  return uniqueConflicts;
};
